import apiClient from './client'

export interface TeamOdds {
  best_price: number | null
  best_book: string | null
  implied_pct: number | null
}

export interface BestTotal {
  line: number
  over_price: number | null
  under_price: number | null
  best_book: string | null
}

export interface GameScheduleEntry {
  game_id: number
  game_date: string
  game_time: string | null
  status: string
  home_team: string
  home_abbreviation: string
  away_team: string
  away_abbreviation: string
  home_score: number | null
  away_score: number | null
  inning: number | null
  home_odds: TeamOdds
  away_odds: TeamOdds
  home_sim_win_pct: number | null
  away_sim_win_pct: number | null
  home_edge: number | null
  away_edge: number | null
  home_lineup_confirmed: boolean
  away_lineup_confirmed: boolean
  home_probable_pitcher: string | null
  away_probable_pitcher: string | null
  best_total: BestTotal | null
  sim_over_pct: number | null
  ou_edge: number | null
}

export interface GamesScheduleResponse {
  games: GameScheduleEntry[]
  from_date: string
  to_date: string
}

export interface PlayerProjectionResponse {
  player_id: number
  name: string
  team: string
  position: string | null
  batting_order: number | null
  mean_hits: number
  mean_hr: number
  mean_rbi: number
  mean_runs: number
  mean_k: number
  mean_bb: number
  hit_distribution: number[]
  hr_distribution: number[]
  k_distribution: number[]
  rbi_distribution: number[]
  bb_distribution: number[]
  runs_distribution: number[]
}

export interface PitcherProjectionResponse {
  player_id: number
  name: string
  team: string
  is_starter: boolean
  mean_ip: number
  mean_k: number
  mean_bb: number
  mean_h: number
  mean_er: number
  mean_hr: number
  ip_distribution: number[]
  k_distribution: number[]
  bb_distribution: number[]
  h_distribution: number[]
  er_distribution: number[]
  hr_distribution: number[]
}

export interface MoneylineEdge {
  best_price: number | null
  best_book: string | null
  implied_pct: number | null
  sim_pct: number | null
  edge: number | null
}

export interface TotalEdge {
  line: number
  over_price: number | null
  under_price: number | null
  best_book: string | null
  implied_over_pct: number | null
  sim_over_pct: number | null
  edge: number | null
}

export interface PropEdge {
  player_id: number
  player_name: string
  team: string
  market: string
  line: number
  over_price: number | null
  under_price: number | null
  best_book: string | null
  implied_over_pct: number | null
  sim_over_pct: number | null
  sim_mean: number | null
  edge: number | null
}

export interface MarketEdges {
  line_kind: string | null // "closing" | "current" | null
  moneyline_home: MoneylineEdge | null
  moneyline_away: MoneylineEdge | null
  total: TotalEdge | null
  prop_edges: PropEdge[]
  top_prop_edges: PropEdge[]
}

export interface SimulationDetailResponse {
  game_id: number
  sim_count: number
  created_at: string
  home_team: string
  home_abbr: string
  away_team: string
  away_abbr: string
  game_date: string
  game_time: string | null
  game_status: string | null
  home_score: number | null
  away_score: number | null
  home_win_pct: number
  away_win_pct: number
  home_run_dist: number[]
  away_run_dist: number[]
  total_run_dist: number[]
  players: PlayerProjectionResponse[]
  pitchers: PitcherProjectionResponse[]
  market: MarketEdges | null
}

export interface ScheduleFilters {
  date_from?: string
  date_to?: string
  team_id?: number
}

export const gamesApi = {
  async getSchedule(filters?: ScheduleFilters): Promise<GamesScheduleResponse> {
    const response = await apiClient.get<GamesScheduleResponse>('/games/schedule', {
      params: filters
    })
    return response.data
  },

  async getTomorrowsGames(): Promise<GamesScheduleResponse> {
    return this.getSchedule()
  },

  async getSimulation(gameId: number): Promise<SimulationDetailResponse> {
    const response = await apiClient.get<SimulationDetailResponse>(`/games/${gameId}/simulation`)
    return response.data
  }
}
